import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Target, Syringe, Feather, Fence } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Icon, Overline, Reveal } from "../components/common";
import { ABOUT_VALUES, IMAGES } from "../data/content";

const POPULATIONS = [
  { icon: Syringe, name: "People who inject drugs (PWID)", stat: "85%", detail: "of all new Hepatitis C infections in Canada occur among people who inject drugs." },
  { icon: Feather, name: "Indigenous communities", stat: "5×", detail: "higher rates of Hepatitis C than the general population in Canada." },
  { icon: Fence,   name: "People who are incarcerated", stat: "24×", detail: "higher rates of Hepatitis C than the general population in Canada." },
];

export default function About() {
  const { t, i18n } = useTranslation();
  const isFr = i18n.language === "fr";
  return (
    <div data-testid="about-page">
      {/* Hero */}
      <section className="relative pt-24 sm:pt-28 pb-8 bg-gradient-to-b from-brand-mist/60 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal>
            <Overline>{t("about.hero.overline")}</Overline>
            <h1 className={`mt-4 text-4xl sm:text-5xl lg:text-5xl xl:text-6xl font-bold tracking-tight text-brand-navy leading-[1.05] max-w-4xl lg:max-w-none ${isFr ? "" : "lg:whitespace-nowrap"}`}>
              {t("about.hero.titleLead")} <span className="text-brand-teal">{t("about.hero.titleAccent")}</span>{" "}
              {isFr ? (
                <>
                  {t("about.hero.titleTrailPart1")}
                  <br className="hidden lg:block" />
                  {t("about.hero.titleTrailPart2")}
                </>
              ) : (
                t("about.hero.titleTrail")
              )}
            </h1>
            <p className="mt-6 text-lg text-slate-500 leading-relaxed max-w-2xl">
              {t("about.hero.subtitle")}
            </p>
          </Reveal>
        </div>
      </section>

      {/* Image + mission */}
      <section className="py-10 sm:py-14 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <Reveal>
              <div className="relative">
                <div className="absolute -bottom-6 -right-6 h-40 w-40 rounded-full bg-brand-teal/15 blur-2xl" />
                <div className="relative rounded-[2rem] overflow-hidden shadow-xl ring-1 ring-black/5">
                  <img
                    src={IMAGES.teamPhoto}
                    alt="A HepCURE team member providing point-of-care testing inside the mobile unit"
                    className="w-full h-[420px] object-cover object-top"
                  />
                </div>
              </div>
            </Reveal>
            <Reveal delay={0.12}>
              <Overline>{t("about.mission.overline")}</Overline>
              <h2 className="mt-3 text-3xl sm:text-4xl font-bold tracking-tight text-brand-navy">
                {t("about.mission.title")}
              </h2>
              <div className="mt-5 space-y-4 text-slate-500 leading-relaxed">
                <p>{t("about.mission.body1")}</p>
                <p>{t("about.mission.body2")}</p>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* Who we serve */}
      <section
        className="py-12 sm:py-16 bg-brand-offwhite"
        data-testid="who-we-serve"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal>
            <div className="max-w-2xl lg:max-w-none">
              <Overline>{t("about.whoWeServe.overline")}</Overline>
              <h2 className="mt-3 text-3xl sm:text-4xl font-bold tracking-tight text-brand-navy lg:whitespace-nowrap">
                {t("about.whoWeServe.title")}
              </h2>
              <p className="mt-5 text-slate-500 leading-relaxed">
                {t("about.whoWeServe.intro")}
              </p>
            </div>
          </Reveal>

          <div className="mt-12 grid md:grid-cols-3 gap-6">
            {POPULATIONS.map((p, i) => {
              const PIcon = p.icon;
              return (
                <Reveal key={p.name} delay={i * 0.1}>
                  <div
                    data-testid={`population-${i}`}
                    className="group relative h-full overflow-hidden rounded-3xl border border-gray-100 bg-white p-8 shadow-sm transition-all duration-300 hover:shadow-lg hover:-translate-y-1"
                  >
                    <div className="absolute -top-10 -right-10 h-28 w-28 rounded-full bg-brand-teal/10 blur-2xl transition-opacity duration-300 group-hover:opacity-100 opacity-0" />
                    <div className="relative flex items-start justify-between gap-4">
                      <div className="h-14 w-14 shrink-0 rounded-2xl bg-brand-navy flex items-center justify-center text-brand-teal-light">
                        <PIcon className="h-7 w-7" strokeWidth={1.6} />
                      </div>
                      <span className="font-heading text-4xl sm:text-5xl font-bold leading-none text-brand-teal">
                        {p.stat}
                      </span>
                    </div>
                    <h3 className="relative mt-6 text-xl font-semibold font-heading text-brand-navy">
                      {p.name}
                    </h3>
                    <p className="relative mt-2 text-slate-500 leading-relaxed">
                      {p.detail}
                    </p>
                  </div>
                </Reveal>
              );
            })}
          </div>

          <Reveal delay={0.2}>
            <p className="mt-8 max-w-3xl text-xs text-slate-400 leading-relaxed">
              {t("about.whoWeServe.citation")}
            </p>
          </Reveal>
        </div>
      </section>

      {/* WHO 2030 highlight */}
      <section className="py-10 sm:py-14 bg-brand-navy">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Reveal>
            <div className="inline-flex h-14 w-14 items-center justify-center rounded-2xl bg-white/10 text-brand-teal-light mb-6">
              <Target className="h-7 w-7" strokeWidth={1.6} />
            </div>
            <h2 className="text-3xl sm:text-4xl font-bold font-heading text-white leading-tight">
              {t("about.who2030.titleLead")}{" "}
              <br className="hidden lg:block" />
              {t("about.who2030.titleAccent")}
            </h2>
            <p className="mt-5 text-white/70 leading-relaxed">
              {t("about.who2030.body")}
            </p>
          </Reveal>
        </div>
      </section>

      {/* Values */}
      <section className="py-12 sm:py-16 bg-brand-offwhite">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal>
            <div className="max-w-2xl lg:max-w-none">
              <Overline>{t("about.values.overline")}</Overline>
              <h2 className="mt-3 text-3xl sm:text-4xl font-bold tracking-tight text-brand-navy lg:whitespace-nowrap">
                {t("about.values.title")}
              </h2>
            </div>
          </Reveal>
          <div className="mt-12 grid md:grid-cols-3 gap-6">
            {ABOUT_VALUES.map((v, i) => {
              const loc = t("content.values", { returnObjects: true }) || [];
              const item = Array.isArray(loc) && loc[i] ? { ...v, ...loc[i] } : v;
              return (
              <Reveal key={v.icon || i} delay={i * 0.1}>
                <div
                  data-testid={`about-value-${i}`}
                  className="bg-white p-8 rounded-2xl border border-gray-100 shadow-sm h-full"
                >
                  <div className="h-12 w-12 rounded-xl bg-brand-teal/10 flex items-center justify-center text-brand-teal">
                    <Icon name={v.icon} className="h-6 w-6" />
                  </div>
                  <h3 className="mt-6 text-xl font-semibold font-heading text-brand-navy">
                    {item.title}
                  </h3>
                  <p className="mt-3 text-slate-500 leading-relaxed">
                    {item.description}
                  </p>
                </div>
              </Reveal>
              );
            })}
          </div>
          <Reveal delay={0.2}>
            <div className="mt-12">
              <Link
                to="/programs"
                data-testid="about-programs-cta"
                className="inline-flex items-center gap-2 rounded-full bg-brand-teal hover:bg-brand-teal-dark text-white px-8 py-3.5 font-semibold transition-colors"
              >
                {t("about.values.cta")}
                <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
